import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Graph } from '@antv/x6';
import { getStroke } from 'perfect-freehand';
import { InkStroke, InkPoint } from '@state';

type InkMode = 'select' | 'draw' | 'erase';

type RenderStroke = {
  id: string;
  color: string;
  d: string;
  fill: string;
  stroke: string;
  strokeWidth: number;
};

@Component({
  selector: 'app-ink-layer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ink-layer.component.html',
  styleUrls: ['./ink-layer.component.scss'],
})
export class InkLayerComponent implements OnChanges, OnDestroy {
  @Input() graph?: Graph | null;
  @Input() mode: InkMode = 'select';
  @Input() color = '#111827';
  @Input() size = 10;
  @Input() strokes: InkStroke[] = [];

  @Output() strokesChange = new EventEmitter<InkStroke[]>();

  @ViewChild('svgRoot', { static: true }) private svgRoot?: ElementRef<SVGSVGElement>;

  viewportTransform = '';
  renderStrokes: RenderStroke[] = [];

  private activeStroke: InkStroke | null = null;
  private pointerId: number | null = null;
  private viewportObserver?: MutationObserver;
  private workingStrokes: InkStroke[] | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graph']) {
      this.attachViewportObserver();
      this.refreshViewportTransform();
    }
    if (changes['strokes'] || changes['size']) {
      this.rebuildPaths();
    }
    if (changes['mode']) {
      // Cancel any in-progress stroke when switching modes.
      if (this.mode !== 'draw' && this.activeStroke) {
        this.activeStroke = null;
        this.pointerId = null;
        this.workingStrokes = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.viewportObserver?.disconnect();
  }

  trackById(_idx: number, s: RenderStroke) {
    return s.id;
  }

  get isInteractive(): boolean {
    return this.mode === 'draw' || this.mode === 'erase';
  }

  onPointerDown(ev: PointerEvent): void {
    if (!this.isInteractive) return;
    if (!this.graph) return;
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();

    const svg = this.svgRoot?.nativeElement;
    try {
      svg?.setPointerCapture(ev.pointerId);
    } catch {}
    this.pointerId = ev.pointerId;

    const p = this.clientToLocal(ev.clientX, ev.clientY);
    if (!p) return;

    if (this.mode === 'erase') {
      const next = this.eraseNear(p);
      if (next !== this.strokes) {
        this.strokesChange.emit(next);
      }
      return;
    }

    const stroke: InkStroke = {
      id: `s_${Math.random().toString(36).slice(2, 10)}`,
      color: this.color,
      size: Math.max(2, Number(this.size) || 10),
      points: [{ x: p.x, y: p.y, p: this.normPressure(ev.pressure), t: Date.now() } satisfies InkPoint],
    };

    this.activeStroke = stroke;
    const base = Array.isArray(this.strokes) ? this.strokes : [];
    const next = [...base, stroke];
    this.workingStrokes = next;
    this.strokesChange.emit(next);
  }

  onPointerMove(ev: PointerEvent): void {
    if (!this.isInteractive) return;
    if (!this.graph) return;
    if (this.pointerId === null || ev.pointerId !== this.pointerId) return;
    if (!this.activeStroke) return;
    ev.preventDefault();
    ev.stopPropagation();

    const p = this.clientToLocal(ev.clientX, ev.clientY);
    if (!p) return;
    const pt: InkPoint = { x: p.x, y: p.y, p: this.normPressure(ev.pressure), t: Date.now() };
    this.activeStroke.points.push(pt);

    // Emit a new array reference so parent can persist; parent should debounce writes.
    const base = this.workingStrokes ?? (Array.isArray(this.strokes) ? this.strokes : []);
    const hasActive = base.some((s) => s.id === this.activeStroke!.id);
    const safeBase = hasActive ? base : [...base, this.activeStroke!];
    const next = safeBase.map((s) => (s.id === this.activeStroke!.id ? { ...this.activeStroke! } : s));
    this.workingStrokes = next;
    this.strokesChange.emit(next);
  }

  onPointerUp(ev: PointerEvent): void {
    if (!this.isInteractive) return;
    if (this.pointerId === null || ev.pointerId !== this.pointerId) return;
    ev.preventDefault();
    ev.stopPropagation();

    try {
      this.svgRoot?.nativeElement?.releasePointerCapture(ev.pointerId);
    } catch {}
    this.pointerId = null;
    this.activeStroke = null;
    this.workingStrokes = null;
  }

  private normPressure(p: number | undefined): number {
    const v = typeof p === 'number' && Number.isFinite(p) && p > 0 ? p : 0.5;
    return Math.max(0.1, Math.min(1, v));
  }

  private clientToLocal(clientX: number, clientY: number): { x: number; y: number } | null {
    const g: any = this.graph as any;
    try {
      if (typeof g.clientToLocal === 'function') {
        const pt = g.clientToLocal(clientX, clientY);
        const x = Number(pt?.x);
        const y = Number(pt?.y);
        if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
      }
    } catch {}
    return null;
  }

  private eraseNear(p: { x: number; y: number }): InkStroke[] {
    const strokes = Array.isArray(this.strokes) ? this.strokes : [];
    if (strokes.length === 0) return strokes;

    const threshold = Math.max(10, Number(this.size) || 10) * 2.2;
    let bestIdx = -1;
    let bestDist = Infinity;

    for (let i = 0; i < strokes.length; i++) {
      const s = strokes[i]!;
      const pts = Array.isArray(s.points) ? s.points : [];
      for (const pt of pts) {
        const dx = (pt?.x ?? 0) - p.x;
        const dy = (pt?.y ?? 0) - p.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
    }

    if (bestIdx === -1 || bestDist > threshold) return strokes;
    const next = strokes.slice();
    next.splice(bestIdx, 1);
    return next;
  }

  private attachViewportObserver(): void {
    this.viewportObserver?.disconnect();
    this.viewportObserver = undefined;

    const viewport = this.viewportEl();
    if (!viewport) return;
    this.viewportObserver = new MutationObserver(() => this.refreshViewportTransform());
    this.viewportObserver.observe(viewport, { attributes: true, attributeFilter: ['transform'] });
  }

  private viewportEl(): SVGGElement | null {
    const container: HTMLElement | null =
      (this.graph as any)?.container ?? (this.graph as any)?.getContainer?.() ?? null;
    if (!container) return null;
    return (container.querySelector('.x6-graph-svg-viewport') as any) ?? null;
  }

  private refreshViewportTransform(): void {
    const viewport = this.viewportEl();
    const t = viewport?.getAttribute?.('transform') ?? '';
    this.viewportTransform = t;
  }

  private rebuildPaths(): void {
    const strokes = Array.isArray(this.strokes) ? this.strokes : [];
    this.renderStrokes = strokes
      .map((s) => {
        const r = this.strokeToPath(s);
        return r ? ({ id: s.id, color: s.color, ...r } satisfies RenderStroke) : null;
      })
      .filter((x): x is RenderStroke => !!x);
  }

  private strokeToPath(
    s: InkStroke
  ): { d: string; fill: string; stroke: string; strokeWidth: number } | null {
    const pts = Array.isArray(s?.points) ? s.points : [];
    if (pts.length < 2) return null;
    const input = pts.map((p) => [Number(p.x), Number(p.y), Number(p.p ?? 0.5)] as any);
    try {
      const outline = getStroke(input as any, {
        size: Math.max(2, Number(s.size) || 10),
        thinning: 0.55,
        smoothing: 0.65,
        streamline: 0.5,
        start: { taper: 0, cap: true },
        end: { taper: 0, cap: true },
      }) as number[][];
      const d = outline.length ? this.svgPathFromPolygon(outline) : '';
      if (!d) return null;
      return { d, fill: s.color, stroke: 'none', strokeWidth: 0 };
    } catch {
      // Fallback: simple polyline stroke outline isn’t available; draw as path line.
      const d = ['M', pts[0]!.x, pts[0]!.y, ...pts.slice(1).flatMap((p) => ['L', p.x, p.y])].join(' ');
      return { d, fill: 'none', stroke: s.color, strokeWidth: Math.max(1, Number(s.size) || 10) };
    }
  }

  private svgPathFromPolygon(points: number[][]): string {
    // Standard helper used with perfect-freehand examples.
    const [first] = points;
    if (!first) return '';
    let d = `M ${first[0]} ${first[1]}`;
    for (let i = 1; i < points.length; i++) {
      const p = points[i]!;
      d += ` L ${p[0]} ${p[1]}`;
    }
    d += ' Z';
    return d;
  }
}

